import { protectedFileClick } from "@/lib/protected-file";
import { useState } from "react";
import { Search, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { DealerApplication } from "@endeks/shared";
import {
  useDealers,
  useDealerStats,
  useUpdateDealerStatus,
  useMarkDealerRead,
  useDeleteDealer,
} from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pending: { label: "Beklemede", className: "bg-orange-100 text-orange-800" },
  approved: { label: "Onaylandı", className: "bg-green-100 text-green-800" },
  rejected: { label: "Reddedildi", className: "bg-red-100 text-red-800" },
};

const TYPE_LABELS: Record<string, string> = {
  tuzel: "Tüzel",
  gercek: "Gerçek",
};

interface DocItem {
  url: string;
  originalName: string;
  label: string;
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, className: "bg-gray-100 text-gray-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.className}`}
    >
      {cfg.label}
    </span>
  );
}

function DescRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b last:border-0">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className="col-span-2 text-sm break-words">{children ?? "-"}</dd>
    </div>
  );
}

function renderDocs(documents: Record<string, DocItem[]> | undefined) {
  if (!documents) return <>-</>;
  const items = Object.values(documents).flat();
  if (items.length === 0) return <>-</>;
  return (
    <ul className="space-y-1">
      {items.map((doc, i) => (
        <li key={i}>
          <a
            href={doc.url}
            onClick={protectedFileClick(doc.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline text-xs"
          >
            {doc.label}: {doc.originalName}
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function DealerList() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<DealerApplication | null>(null);

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const {
    data: rawDealers,
    isLoading,
    isError,
    error,
    refetch,
  } = useDealers({
    search: debouncedSearch || undefined,
    status: statusFilter,
  });
  const dealers: DealerApplication[] = rawDealers ?? [];
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(
    dealers,
    20,
    `${debouncedSearch}|${statusFilter ?? ""}`,
  );

  const { data: stats } = useDealerStats();
  const updateStatus = useUpdateDealerStatus();
  const markRead = useMarkDealerRead();
  const deleteMutation = useDeleteDealer();

  function handleOpen(record: DealerApplication) {
    setSelected(record);
    if (!record.read) {
      markRead.mutate(record.id);
    }
  }

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Başvuru silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  function handleStatusChange(value: string) {
    if (!selected) return;
    updateStatus.mutate(
      { id: selected.id, status: value },
      {
        onSuccess: () => {
          setSelected({ ...selected, status: value });
          toast.success("Durum güncellendi");
        },
        onError: (e: unknown) => toast.error(errorMessage(e, "Durum güncellenemedi")),
      },
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold">İş Ortağı Başvuruları</h1>
          {stats && stats.pending > 0 && (
            <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">
              {stats.pending} beklemede
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Status filter */}
          <Select
            value={statusFilter ?? "all"}
            onValueChange={(v) => setStatusFilter(v === "all" ? undefined : v)}
          >
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Durum" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="pending">Beklemede</SelectItem>
              <SelectItem value="approved">Onaylandı</SelectItem>
              <SelectItem value="rejected">Reddedildi</SelectItem>
            </SelectContent>
          </Select>
          {/* Search */}
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawDealers ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : dealers.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {debouncedSearch || statusFilter ? "Arama sonucu bulunamadı." : "Henüz başvuru yok."}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-6" />
                <TableHead>Şirket</TableHead>
                <TableHead>Yetkili</TableHead>
                <TableHead>E-posta</TableHead>
                <TableHead className="w-24">Tip</TableHead>
                <TableHead className="w-28">Durum</TableHead>
                <TableHead className="w-28">Tarih</TableHead>
                <TableHead className="w-24 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((dealer) => (
                <TableRow
                  key={dealer.id}
                  className={!dealer.read ? "font-medium bg-blue-50/50" : undefined}
                >
                  <TableCell>
                    {!dealer.read && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                  </TableCell>
                  <TableCell>{dealer.companyName}</TableCell>
                  <TableCell>{dealer.authorizedName}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{dealer.email}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
                      {TYPE_LABELS[dealer.applicationType] || dealer.applicationType}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={dealer.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(dealer.createdAt).toLocaleDateString("tr-TR")}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleOpen(dealer)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleDelete(dealer.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Başvuru Detayı</DialogTitle>
          </DialogHeader>
          {selected && (
            <dl className="divide-y">
              <DescRow label="Durum">
                <Select
                  value={selected.status}
                  onValueChange={handleStatusChange}
                  disabled={updateStatus.isPending}
                >
                  <SelectTrigger className="w-40 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Beklemede</SelectItem>
                    <SelectItem value="approved">Onaylandı</SelectItem>
                    <SelectItem value="rejected">Reddedildi</SelectItem>
                  </SelectContent>
                </Select>
              </DescRow>
              <DescRow label="Başvuru Tipi">
                {TYPE_LABELS[selected.applicationType] || selected.applicationType}
              </DescRow>
              <DescRow label="Şirket Adı">{selected.companyName}</DescRow>
              <DescRow label="Yetkili">{selected.authorizedName}</DescRow>
              <DescRow label="TCKN">{selected.tckn}</DescRow>
              <DescRow label="E-posta">{selected.email}</DescRow>
              <DescRow label="Mersis No">{selected.mersisNo}</DescRow>
              <DescRow label="Telefon">{selected.phone}</DescRow>
              <DescRow label="Mobil">{selected.mobile}</DescRow>
              <DescRow label="Fax">{selected.fax}</DescRow>
              <DescRow label="Adres">
                {selected.address && (
                  <>
                    {selected.address}
                    <br />
                    {[
                      selected.neighborhood,
                      selected.district,
                      "/",
                      selected.province,
                      selected.postalCode,
                      selected.country,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </>
                )}
              </DescRow>
              <DescRow label="Vergi Dairesi">{selected.taxOffice}</DescRow>
              <DescRow label="Vergi No">{selected.taxNo}</DescRow>
              <DescRow label="Para Birimi">{selected.currency}</DescRow>
              <DescRow label="IBAN">{selected.iban}</DescRow>
              <DescRow label="Pazarlama İzni">{selected.marketingOptIn ? "Evet" : "Hayır"}</DescRow>
              <DescRow label="Belgeler">
                {renderDocs(selected.documents as Record<string, DocItem[]> | undefined)}
              </DescRow>
              <DescRow label="Tarih">
                {new Date(selected.createdAt).toLocaleString("tr-TR")}
              </DescRow>
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
